import React from 'react';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Box from '@material-ui/core/Box';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { t } from 'ttag';
import useReactRouter from 'use-react-router';
import { useApolloClient } from '@apollo/react-hooks';
import {
  Close as CloseIcon,
  GetApp as GetAppIcon,
  PlayArrow as PlayArrowIcon,
} from '@material-ui/icons';
import IconButton from '@material-ui/core/IconButton';

import { MyClubCApi } from '../../../api';
import Loading from '../../../components/Loading';
import { stripHtmlContent, toRichTextHtml } from '../../../components/RichTextEditor';
import {
  convertToYouTubeEmbed,
  fetchYouTubeTitle,
  getYouTubeThumbnailUrl,
  YOUTUBE_EMBED_URL_PATTERN,
} from '../../../utils/youtube';
import { getPublicServerUrl } from '../../../utils/urls';

const useVideoStyles = makeStyles((theme) => ({
  grid: {
    marginTop: theme.spacing(1),
  },
  thumbButton: {
    display: 'block',
    width: '100%',
    padding: 0,
    border: '1px solid',
    borderColor: theme.palette.grey[300],
    borderRadius: 10,
    overflow: 'hidden',
    background: theme.palette.common.white,
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    },
  },
  thumbWrap: {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%',
    backgroundColor: theme.palette.grey[200],
  },
  thumbImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  playOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.28)',
  },
  playIcon: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.92)',
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbCaption: {
    padding: theme.spacing(1, 1.25),
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.35,
    minHeight: 40,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  expandedCard: {
    border: '1px solid',
    borderColor: theme.palette.primary.main,
    borderRadius: 10,
    // overflow:hidden ламає touch по YouTube iframe на iOS/Android
    background: theme.palette.common.white,
    boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
  },
  expandedHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1, 1, 1.25),
  },
  expandedTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.35,
    paddingRight: theme.spacing(1),
    flex: 1,
  },
  player: {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%',
    backgroundColor: '#000',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    // окремий шар під iframe — touch йде в плеєр, а не в батьківський scroll/accordion
    WebkitTransform: 'translateZ(0)',
    transform: 'translateZ(0)',
  },
  iframe: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 0,
    zIndex: 2,
    // iOS Safari: дозволяє кліки/паузу в embed
    pointerEvents: 'auto',
    touchAction: 'manipulation',
  },
  externalLink: {
    display: 'inline-block',
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(2),
  },
}));

const hasRichText = (value) => stripHtmlContent(value || '').length > 0;

const buildYoutubeVideos = (links, itemNameText) => {
  const youtubeVideos = [];
  const externalLinks = [];

  links.forEach((link, li) => {
    const maybeEmbedLink = convertToYouTubeEmbed(link);
    if (YOUTUBE_EMBED_URL_PATTERN.test(maybeEmbedLink)) {
      const fallbackLabel =
        links.length > 1 ? `${t`Відео`} ${li + 1}` : t`Відео`;
      youtubeVideos.push({
        key: String(li),
        link,
        embedUrl: maybeEmbedLink,
        thumbnailUrl: getYouTubeThumbnailUrl(maybeEmbedLink),
        fallbackLabel,
        title: itemNameText
          ? `${itemNameText} — ${fallbackLabel}`
          : fallbackLabel,
      });
    } else {
      externalLinks.push({ key: String(li), link, index: li });
    }
  });

  return { youtubeVideos, externalLinks };
};

const DankaiVideoGallery = ({
  links,
  itemNameText,
  activeVideoKey,
  onSelectVideo,
  videoColumns,
  classes,
}) => {
  const { youtubeVideos, externalLinks } = React.useMemo(
    () => buildYoutubeVideos(links, itemNameText),
    [links, itemNameText]
  );
  const [titlesByKey, setTitlesByKey] = React.useState({});

  React.useEffect(() => {
    let cancelled = false;

    const loadTitles = async () => {
      if (!youtubeVideos.length) {
        return;
      }
      const baseUrl = getPublicServerUrl();
      const entries = await Promise.all(
        youtubeVideos.map(async (video) => {
          const title = await fetchYouTubeTitle(video.embedUrl, { baseUrl });
          return [video.key, title];
        })
      );
      if (cancelled) {
        return;
      }
      const next = {};
      entries.forEach(([key, title]) => {
        if (title) {
          next[key] = title;
        }
      });
      setTitlesByKey(next);
    };

    loadTitles();
    return () => {
      cancelled = true;
    };
  }, [youtubeVideos]);

  if (!youtubeVideos.length && !externalLinks.length) {
    return null;
  }

  const thumbItemProps =
    videoColumns === 3
      ? { xs: 12, sm: 6, md: 4 }
      : videoColumns === 2
        ? { xs: 12, sm: 6 }
        : { xs: 12 };

  return (
    <Box mt={2} width="100%">
      {youtubeVideos.length > 0 && (
        <Grid container spacing={2} className={classes.grid}>
          {youtubeVideos.map((video) => {
            const isActive = activeVideoKey === video.key;
            const label = titlesByKey[video.key] || video.fallbackLabel;

            if (isActive) {
              return (
                <Grid item xs={12} key={video.key}>
                  <div className={classes.expandedCard}>
                    <div className={classes.expandedHeader}>
                      <div className={classes.expandedTitle}>{label}</div>
                      <IconButton
                        size="small"
                        aria-label={t`Згорнути відео`}
                        onClick={() => onSelectVideo && onSelectVideo(video.key)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </div>
                    <div
                      className={classes.player}
                      onClick={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                    >
                      <iframe
                        src={`${video.embedUrl}?autoplay=1&playsinline=1&rel=0&modestbranding=1`}
                        title={label}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className={classes.iframe}
                      />
                    </div>
                  </div>
                </Grid>
              );
            }

            return (
              <Grid item {...thumbItemProps} key={video.key}>
                <button
                  type="button"
                  className={classes.thumbButton}
                  onClick={() => onSelectVideo && onSelectVideo(video.key)}
                  aria-pressed={false}
                  title={label}
                >
                  <div className={classes.thumbWrap}>
                    {video.thumbnailUrl ? (
                      <img
                        src={video.thumbnailUrl}
                        alt={label}
                        className={classes.thumbImage}
                      />
                    ) : null}
                    <div className={classes.playOverlay}>
                      <div className={classes.playIcon}>
                        <PlayArrowIcon />
                      </div>
                    </div>
                  </div>
                  <div className={classes.thumbCaption}>{label}</div>
                </button>
              </Grid>
            );
          })}
        </Grid>
      )}

      {externalLinks.map((itemLink) => (
        <a
          key={itemLink.key}
          href={itemLink.link}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.externalLink}
        >
          {externalLinks.length > 1
            ? `${t`Посилання`} ${itemLink.index + 1}`
            : t`Посилання`}
        </a>
      ))}
    </Box>
  );
};

const InfoView = () => {
  const classes = useVideoStyles();
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const videoColumns = isMdUp ? 3 : isSmUp ? 2 : 1;
  const { match, history } = useReactRouter();
  const client = useApolloClient();
  const api = React.useMemo(() => new MyClubCApi(client), [client]);

  const dankaiIdParam = match.params.id || '';
  const dankaiId = Number(dankaiIdParam);
  const hasValidId = Number.isFinite(dankaiId) && !Number.isNaN(dankaiId);

  const [loading, setLoading] = React.useState(true);
  const [dankai, setDankai] = React.useState(null);
  const [errorDialog, setErrorDialog] = React.useState('');
  const [activeVideoByItem, setActiveVideoByItem] = React.useState({});
  const [generatingPdf, setGeneratingPdf] = React.useState(false);

  const openPdf = React.useCallback((url) => {
    if (!url) {
      return;
    }

    const ua = window.navigator.userAgent || '';
    const platform = window.navigator.platform || '';
    const maxTouchPoints = window.navigator.maxTouchPoints || 0;
    const isIos = /iP(ad|hone|od)/.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1);

    if (isIos) {
      window.location.href = url;
      return;
    }

    const newWindow = window.open(url, '_blank');
    if (newWindow) {
      newWindow.focus();
    } else {
      window.location.href = url;
    }
  }, []);

  const handleGeneratePdf = React.useCallback(async () => {
    if (!dankai || generatingPdf) {
      return;
    }

    if (!dankai.id) {
      setErrorDialog(t`Невірний данкай`);
      return;
    }

    setGeneratingPdf(true);
    try {
      const response = await api.generateDankaiPdf({ id: dankai.id });
      
      if (response.ok) {
        if (response.url) {
          // Відкриваємо PDF з урахуванням обмежень iOS Safari
          openPdf(response.url);
        } else {
          setErrorDialog(t`Не вдалося згенерувати PDF`);
        }
      } else {
        let errorMessage = t`Не вдалося згенерувати PDF`;
        if (response.errors && response.errors.length > 0) {
          const firstError = response.errors[0];
          if (firstError.messages && firstError.messages.length > 0) {
            errorMessage = firstError.messages[0];
          } else if (firstError.field) {
            errorMessage = `${t`Помилка в полі`} ${firstError.field}`;
          }
        }
        setErrorDialog(errorMessage);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate PDF', error);
      const errorMessage = error.message || error.toString() || t`Не вдалося згенерувати PDF`;
      setErrorDialog(errorMessage);
    } finally {
      setGeneratingPdf(false);
    }
  }, [api, dankai, generatingPdf, openPdf]);

  const getItemLinks = React.useCallback((item = {}) => {
    if (Array.isArray(item.links) && item.links.length) {
      return item.links.map((link) => (link || '').trim()).filter(Boolean);
    }
    if (item.link) {
      return [item.link];
    }
    return [];
  }, []);

  const renderItemLinks = React.useCallback(
    (item, options = {}) => {
      const links = getItemLinks(item);
      if (!links.length) {
        return null;
      }

      return (
        <DankaiVideoGallery
          links={links}
          itemNameText={stripHtmlContent(item.name || '')}
          activeVideoKey={options.activeVideoKey || null}
          onSelectVideo={options.onSelectVideo}
          videoColumns={videoColumns}
          classes={classes}
        />
      );
    },
    [classes, getItemLinks, videoColumns]
  );

  React.useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      if (!hasValidId) {
        setLoading(false);
        setErrorDialog(t`Невірний ідентифікатор данкаю`);
        return;
      }

      setLoading(true);
      try {
        const detail = await api.getDankai({ dankaiId });
        if (isCancelled) {
          return;
        }
        if (!detail) {
          setErrorDialog(t`Данкай не знайдено`);
          return;
        }
        setDankai(detail);
      } catch (err) {
        if (!isCancelled) {
          // eslint-disable-next-line no-console
          console.error('Failed to load dankai info', err);
          setErrorDialog(t`Не вдалося завантажити данкай`);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      isCancelled = true;
    };
  }, [api, dankaiId, hasValidId]);

  if (loading) {
    return <Loading />;
  }

  if (!dankai) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h6">{t`Данкай не знайдено`}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography variant="h4" component="h1" style={{ marginRight: "50px", fontSize: "5rem", whiteSpace: "nowrap", flexShrink: 0 }}>
            {dankai.name}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={handleGeneratePdf}
            disabled={generatingPdf}
            style={{ marginTop: 16 }}
          >
            {generatingPdf ? t`Генерація PDF...` : t`Згенерувати PDF`}
          </Button>
        </Box>
        <Box mt={{ xs: 2, sm: 0 }} textAlign="left" width={{ xs: '100%', sm: 'auto' }}>
          {dankai.theme && (
            <Typography>
              <Box component="span" fontWeight={600}>
                {t`Тема:`}
              </Box>{' '}
              {dankai.theme}
            </Typography>
          )}
          {dankai.criteria && (
            <Typography>
              <Box component="span" fontWeight={600}>
                {t`Критерії:`}
              </Box>{' '}
              {dankai.criteria}
            </Typography>
          )}
        </Box>
      </Box>
      <Box mt={3}>
        {(dankai.sections || []).map((section, si) => (
          <Paper key={section.id || si} elevation={1} style={{ marginBottom: 16 }}>
            <ExpansionPanel defaultExpanded>
              <ExpansionPanelSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`section-${si}-content`}
                id={`section-${si}-header`}
              >
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  {`${si + 1}. ${section.name}`}
                </Typography>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Box width="100%">
                  {hasRichText(section.description) && (
                    <Typography
                      gutterBottom
                      component="div"
                      dangerouslySetInnerHTML={{ __html: toRichTextHtml(section.description) }}
                    />
                  )}
                  <Grid container spacing={0}>

                    {(section.items || []).map((item, ii) => {
                      const itemKey = item.id || `${si}-${ii}`;
                      const itemLinks = getItemLinks(item);
                      const hasYoutubeLinks = itemLinks.some((link) =>
                        YOUTUBE_EMBED_URL_PATTERN.test(convertToYouTubeEmbed(link))
                      );
                      const linkNode = hasYoutubeLinks
                        ? renderItemLinks(item, {
                            activeVideoKey: activeVideoByItem[itemKey] || null,
                            onSelectVideo: (videoKey) =>
                              setActiveVideoByItem((prev) => ({
                                ...prev,
                                [itemKey]:
                                  prev[itemKey] === videoKey ? null : videoKey,
                              })),
                          })
                        : renderItemLinks(item);

                      const hasDescription = hasRichText(item.description);
                      const hasTip = Boolean(item.tip);
                      const hasLink = itemLinks.length > 0;
                      const hasExpandableContent = hasDescription || hasTip || hasLink;

                      return (
                        <Grid item xs={12} key={item.id || ii}>
                          {hasExpandableContent ? (
                            <ExpansionPanel elevation={0}>
                              <ExpansionPanelSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`section-${si}-item-${ii}-content`}
                                id={`section-${si}-item-${ii}-header`}
                              >
                                <Grid container spacing={1} alignItems="flex-start">
                                  <Grid item style={{ flexShrink: 0 }}>
                                    <Typography variant="subtitle2" style={{ fontWeight: "normal" }}>
                                      {ii + 1}.
                                    </Typography>
                                  </Grid>
                                  <Grid item style={{ flex: 1 }}>
                                    <Typography
                                      variant="subtitle2"
                                      style={{ fontWeight: "normal" }}
                                      component="div"
                                      dangerouslySetInnerHTML={{ __html: toRichTextHtml(item.name) }}
                                    />
                                  </Grid>
                                </Grid>
                              </ExpansionPanelSummary>
                              <ExpansionPanelDetails>
                                <Box width="100%">
                                  {hasDescription && (
                                    <Typography
                                      paragraph
                                      component="div"
                                      style={{ fontStyle: 'italic' }}
                                      dangerouslySetInnerHTML={{ __html: toRichTextHtml(item.description) }}
                                    />
                                  )}
                                  {hasTip && (
                                    <Typography variant="body2" paragraph>
                                      {t`Порада:`} {item.tip}
                                    </Typography>
                                  )}
                                  {linkNode}
                                </Box>
                              </ExpansionPanelDetails>
                            </ExpansionPanel>
                          ) : (
                            <Box p={2} >
                              <Grid container spacing={1} alignItems="flex-start">
                                <Grid item style={{ flexShrink: 0 }}>
                                  <Typography variant="subtitle2" style={{ fontWeight: "normal" }}>
                                    {ii + 1}.
                                  </Typography>
                                </Grid>
                                <Grid item style={{ flex: 1 }}>
                                  <Typography
                                    variant="subtitle2"
                                    style={{ fontWeight: "normal" }}
                                    component="div"
                                    dangerouslySetInnerHTML={{ __html: toRichTextHtml(item.name) }}
                                  />
                                </Grid>
                              </Grid>
                            </Box>
                          )}
                        </Grid>
                      );
                    })}

                  </Grid>
                </Box>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          </Paper>
        ))}
      </Box>

      <Dialog open={Boolean(errorDialog)} onClose={() => {
        setErrorDialog('');
        history.goBack();
      }}>
        <DialogTitle>{t`Помилка`}</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorDialog}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            onClick={() => {
              setErrorDialog('');
              history.goBack();
            }}
          >
            {t`Повернутись`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InfoView;
